// Display formatting for the operator data screens.
//
// IMPORTANT: every datetime the API returns is already tenant-LOCAL naive
// ('YYYY-MM-DDTHH:mm' — see the timezone convention in CLAUDE.md). So these
// helpers must NEVER re-convert through the browser timezone; they parse the
// string's own parts and format those. Passing such a string to `new Date()` and
// calling toLocaleString() would shift it by the viewer's offset.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

interface Parts { y: number; mo: number; d: number; h: number; mi: number }

function parseNaive(value: string | null | undefined): Parts | null {
  if (!value) return null
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/)
  if (!m) return null
  return {
    y: Number(m[1]),
    mo: Number(m[2]),
    d: Number(m[3]),
    h: m[4] ? Number(m[4]) : 0,
    mi: m[5] ? Number(m[5]) : 0,
  }
}

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

/** '14 Mar 2026' */
export function fmtDate(value: string | null | undefined): string {
  const p = parseNaive(value)
  if (!p) return '—'
  return `${p.d} ${MONTHS[p.mo - 1]} ${p.y}`
}

/** '14 Mar 2026, 10:00' */
export function fmtDateTime(value: string | null | undefined): string {
  const p = parseNaive(value)
  if (!p) return '—'
  return `${p.d} ${MONTHS[p.mo - 1]} ${p.y}, ${pad(p.h)}:${pad(p.mi)}`
}

/** '10:00' */
export function fmtTime(value: string | null | undefined): string {
  const p = parseNaive(value)
  if (!p) return '—'
  return `${pad(p.h)}:${pad(p.mi)}`
}

/** 'Sat 14 Mar' — weekday computed from the local date parts, not a Date shift. */
export function fmtDayDate(value: string | null | undefined): string {
  const p = parseNaive(value)
  if (!p) return '—'
  // Zeller-free: build a UTC date from the naive parts so getUTCDay() is stable.
  const dow = new Date(Date.UTC(p.y, p.mo - 1, p.d)).getUTCDay()
  const weekday = WEEKDAYS[(dow + 6) % 7]
  return `${weekday} ${p.d} ${MONTHS[p.mo - 1]}`
}

/** 'HH:mm' from a bare Postgres time value ('09:00:00'). */
export function fmtClock(value: string | null | undefined): string {
  if (!value) return '—'
  const m = String(value).match(/^(\d{1,2}):(\d{2})/)
  return m ? `${pad(Number(m[1]))}:${m[2]}` : String(value)
}

/** Day-of-week number (1=Mon…7=Sun) → 'Monday'. */
const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
export function dayName(dow: number): string {
  return DAY_NAMES[dow - 1] ?? `Day ${dow}`
}

/** '$1,234.50' — currency symbol only for the ones this platform actually uses. */
const SYMBOLS: Record<string, string> = { AUD: '$', USD: '$', NZD: '$', GBP: '£', EUR: '€' }
export function fmtMoney(
  amount: number | string | null | undefined,
  currency = 'AUD',
  opts?: { compact?: boolean },
): string {
  if (amount === null || amount === undefined || amount === '') return '—'
  const n = Number(amount)
  if (!Number.isFinite(n)) return '—'
  const symbol = SYMBOLS[currency] ?? ''
  if (opts?.compact && Math.abs(n) >= 1000) {
    const k = n / 1000
    return `${symbol}${k >= 100 ? Math.round(k) : k.toFixed(1)}k`
  }
  return `${symbol}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/** 12345 → '12,345' */
export function fmtCount(n: number | null | undefined): string {
  return n === null || n === undefined ? '—' : n.toLocaleString()
}

/** 90 → '1h 30m' */
export function fmtDuration(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return '—'
  const m = Math.round(Number(minutes))
  if (!Number.isFinite(m)) return '—'
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  const rem = m % 60
  return rem ? `${h}h ${rem}m` : `${h}h`
}

/**
 * A notice period in the unit a person would say it in: 1440 → '24 hours',
 * 2880 → '2 days', 90 → '1 hour 30 minutes'.
 *
 * Deliberately NOT fmtDuration: that renders '24h', which is right on a booking
 * tile and wrong in a sentence an operator reads to decide whether they have
 * just taken a treatment off sale for a fortnight. Whole days and whole hours
 * get their own words because a misplaced zero is invisible in '1440' and
 * obvious in '10 days'.
 */
export function fmtNotice(minutes: number | null | undefined): string {
  const m = Math.round(Number(minutes))
  if (!Number.isFinite(m) || m <= 0) return 'no notice'
  const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'}`
  if (m % 1440 === 0) return plural(m / 1440, 'day')
  if (m % 60 === 0) return plural(m / 60, 'hour')
  if (m > 60) {
    const h = Math.floor(m / 60)
    return `${plural(h, 'hour')} ${plural(m % 60, 'minute')}`
  }
  return plural(m, 'minute')
}

/** '2026-03' → 'Mar 26' for compact chart axes. */
export function fmtMonthLabel(month: string): string {
  const m = month.match(/^(\d{4})-(\d{2})$/)
  if (!m) return month
  return `${MONTHS[Number(m[2]) - 1]} ${m[1].slice(2)}`
}

/** Phone / name fallback so rows never render blank. */
export function displayName(name?: string | null, phone?: string | null): string {
  return name?.trim() || phone?.trim() || 'Unnamed'
}
